import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { recallApi } from '@/utils/api';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Video, RefreshCw, Trash2, ArrowRight, Loader2,
  Calendar, ExternalLink, Bot, AlertCircle,
} from 'lucide-react';
import { BLUE, BLUE_D, INK, MUTE, FAINT, LINE, CARD_ALT, GREEN, SERIF } from '@/styles/milk';

// Map Recall bot status -> label + color
function humanizeStatus(s) {
  return (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
function statusMeta(status) {
  const s = (status || '').toLowerCase();
  if (s === 'done') return { label: 'Done', color: GREEN, bg: 'rgba(54,156,98,.12)' };
  if (s.includes('fatal') || s.includes('error') || s.includes('denied')) return { label: 'Failed', color: '#B4332E', bg: 'rgba(180,51,46,.10)' };
  if (s.includes('call_ended') || s === 'call_ended') return { label: 'Processing', color: BLUE_D, bg: 'rgba(103,150,194,.12)' };
  if (s.includes('recording')) return { label: 'Recording', color: BLUE_D, bg: 'rgba(103,150,194,.14)' };
  if (s === 'scheduled') return { label: 'Scheduled', color: '#B8860B', bg: 'rgba(184,134,11,.12)' };
  if (s.includes('joining') || s.includes('waiting')) return { label: 'Joining…', color: BLUE_D, bg: 'rgba(103,150,194,.12)' };
  return { label: humanizeStatus(status) || 'Pending', color: MUTE, bg: 'rgba(33,46,64,.06)' };
}

export default function MeetingsPage() {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meetingUrl, setMeetingUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [syncing, setSyncing] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await recallApi.list();
      setBots(res.data || []);
    } catch {
      toast.error('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Light auto-refresh while any bot is still in a non-terminal state
  useEffect(() => {
    const active = bots.some(b => {
      const s = (b.status || '').toLowerCase();
      return !b.action_card_id && !s.includes('fatal') && !s.includes('error');
    });
    if (!active) return;
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [bots, load]);

  const sendBot = async (e) => {
    e.preventDefault();
    const url = meetingUrl.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      toast.error('Enter a valid meeting link (Zoom, Google Meet, or Teams).');
      return;
    }
    setSending(true);
    try {
      await recallApi.send({ meeting_url: url });
      toast.success('Notetaker is on its way to the meeting');
      setMeetingUrl('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send the bot');
    } finally {
      setSending(false);
    }
  };

  const syncBot = async (id) => {
    setSyncing(p => ({ ...p, [id]: true }));
    try {
      const res = await recallApi.sync(id);
      const cardId = res.data?.action_card_id;
      if (cardId) toast.success('Meeting processed — Action Card ready!');
      else {
        const st = (res.data?.bot?.status || '').toLowerCase();
        if (/done|call_ended|processing/.test(st)) toast.info('Transcript still generating — check back in a moment.');
        else toast.info(`Status: ${statusMeta(res.data?.bot?.status).label}`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not refresh status');
    } finally {
      setSyncing(p => ({ ...p, [id]: false }));
    }
  };

  const removeBot = async (id) => {
    try {
      await recallApi.remove(id);
      setBots(prev => prev.filter(b => b.id !== id));
      toast.success('Removed from list');
    } catch {
      toast.error('Could not remove');
    }
  };

  return (
    <div className="pagewrap" data-testid="meetings-page" style={{ fontFamily: "'Work Sans', sans-serif" }}>
      <div className="page-head">
        <div>
          <h1 className="pt">Meetings</h1>
          <p className="ps">Send a notetaker bot to your video calls — it joins, records, and turns the conversation into an Action Card.</p>
        </div>
      </div>

      {/* Send-bot form */}
      <form
        onSubmit={sendBot}
        data-testid="meeting-send-form"
        className="rounded-2xl p-5 mb-7"
        style={{ background: '#FFFFFF', border: `1px solid ${LINE}`, boxShadow: '0 1px 2px rgba(33,46,64,.04)' }}
      >
        <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-3" style={{ color: FAINT, letterSpacing: '0.08em' }}>
          <Video size={13} style={{ color: BLUE }} />Meeting link
        </label>
        <div className="flex flex-col sm:flex-row gap-2.5">
          <input
            value={meetingUrl}
            onChange={e => setMeetingUrl(e.target.value)}
            placeholder="Paste a Zoom, Google Meet or Teams link…"
            data-testid="meeting-url-input"
            className="flex-1 h-11 px-3.5 rounded-xl text-sm outline-none"
            style={{ background: CARD_ALT, border: `1px solid ${LINE}`, color: INK }}
          />
          <button
            type="submit"
            disabled={sending || !meetingUrl.trim()}
            data-testid="meeting-send-button"
            className="h-11 px-5 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-1.5 disabled:opacity-50 shrink-0"
            style={{ background: BLUE, boxShadow: '0 6px 16px rgba(103,150,194,.3)' }}
          >
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Bot size={15} />}
            {sending ? 'Sending…' : 'Send notetaker'}
          </button>
        </div>
        <p className="text-xs mt-2.5" style={{ color: FAINT }}>
          The bot appears as a participant named “Milk Notetaker.” Make sure recording is allowed for the meeting.
        </p>
      </form>

      {/* Bot list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2].map(i => <Skeleton key={i} className="h-20 rounded-2xl" style={{ background: 'rgba(33,46,64,.06)' }} />)}
        </div>
      ) : bots.length === 0 ? (
        <div
          className="rounded-3xl py-14 text-center"
          style={{ background: '#FFFFFF', border: `1px solid ${LINE}` }}
          data-testid="meetings-empty"
        >
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(103,150,194,0.10)' }}>
            <Video size={20} style={{ color: BLUE }} />
          </div>
          <p className="text-base font-semibold mb-1.5" style={{ color: INK }}>No meetings yet</p>
          <p className="text-sm max-w-sm mx-auto" style={{ color: MUTE }}>
            Paste a meeting link above and Milk will send a notetaker bot to capture it for you.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {bots.map(bot => {
            const isSyncing = syncing[bot.id];
            const hasCard = !!bot.action_card_id;
            const s = (bot.status || '').toLowerCase();
            const isFailed = /fatal|error|denied/.test(s);
            const isFinished = /done|call_ended|processing/.test(s);
            const pending = !hasCard && isFinished && !isFailed;
            const meta = pending
              ? { label: 'Transcript pending', color: '#B8860B', bg: 'rgba(184,134,11,.12)' }
              : statusMeta(bot.status);
            return (
              <div
                key={bot.id}
                data-testid="meeting-bot-row"
                className="rounded-2xl p-4 sm:p-5 flex items-start gap-4"
                style={{ background: '#FFFFFF', border: `1px solid ${LINE}`, boxShadow: '0 1px 2px rgba(33,46,64,.04)' }}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(103,150,194,0.10)' }}>
                  <Video size={17} style={{ color: BLUE }} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: meta.bg, color: meta.color }}>
                      {meta.label}
                    </span>
                    <span className="text-xs flex items-center gap-1" style={{ color: FAINT }}>
                      <Calendar size={11} />
                      {new Date(bot.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </span>
                  </div>
                  <a
                    href={bot.meeting_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium flex items-center gap-1 truncate"
                    style={{ color: INK }}
                  >
                    <span className="truncate">{bot.meeting_url}</span>
                    <ExternalLink size={11} style={{ color: FAINT, flexShrink: 0 }} />
                  </a>

                  {pending && (
                    <p className="text-xs mt-1.5 flex items-center gap-1.5" style={{ color: '#B8860B' }} data-testid="meeting-transcript-pending">
                      <Loader2 size={11} className="animate-spin" />
                      Transcript pending — the Action Card is still being generated.
                    </p>
                  )}
                  {isFailed && !hasCard && (
                    <p className="text-xs mt-1.5 flex items-center gap-1.5" style={{ color: '#B4332E' }} data-testid="meeting-failed-hint">
                      <AlertCircle size={11} />
                      The recording failed. Try reprocessing, or remove it.
                    </p>
                  )}

                  <div className="flex items-center gap-3 mt-2.5 flex-wrap">
                    {bot.action_card_id ? (
                      <Link
                        to={`/action-cards/${bot.action_card_id}`}
                        data-testid="meeting-view-card-link"
                        className="text-xs font-semibold flex items-center gap-1"
                        style={{ color: BLUE_D }}
                      >
                        View Action Card <ArrowRight size={12} />
                      </Link>
                    ) : (
                      <button
                        onClick={() => syncBot(bot.id)}
                        disabled={isSyncing}
                        data-testid="meeting-reprocess-button"
                        className="text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
                        style={{ color: BLUE_D }}
                      >
                        <RefreshCw size={12} className={isSyncing ? 'animate-spin' : ''} />
                        {isSyncing ? 'Reprocessing…' : (pending || isFailed ? 'Reprocess' : 'Check status')}
                      </button>
                    )}
                    {bot.recording_url && (
                      <a href={bot.recording_url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold flex items-center gap-1" style={{ color: MUTE }}>
                        Recording <ExternalLink size={10} />
                      </a>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => removeBot(bot.id)}
                  data-testid="meeting-remove-button"
                  className="p-1.5 rounded-lg shrink-0"
                  style={{ color: FAINT }}
                  title="Remove from list"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
